import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { toast } from "sonner";
import { useMemo, useState } from "react";
import { useIngredientCategoriesLookupQuery } from "../api/ingredient-categories-queries";
import { useBulkAssignIngredientCategory } from "../api/queries";

const NONE = "__none__" as const;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ingredientIds: string[];
  onSuccess: () => void;
};

export function IngredientsBulkCategoryDialog({
  open,
  onOpenChange,
  ingredientIds,
  onSuccess,
}: Props) {
  const lookup = useIngredientCategoriesLookupQuery();
  const bulk = useBulkAssignIngredientCategory();
  const [categoryValue, setCategoryValue] = useState<string>(NONE);

  const options = useMemo(
    () => [
      { value: NONE, label: "ຍັງບໍ່ຈັດຫມວດ (ລຶກຫມວດ)" },
      ...(lookup.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ],
    [lookup.data],
  );

  const handleSubmit = async () => {
    const categoryId =
      categoryValue === NONE || !categoryValue ? null : categoryValue;
    try {
      await bulk.mutateAsync({ ingredientIds, categoryId });
      onSuccess();
      onOpenChange(false);
      setCategoryValue(NONE);
    } catch {
      toast.error("ມອບຫມວດລົ້ມເຫຼວ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>ມອບຫມວດວັດຖຸດິບ</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          ເລືອກ {ingredientIds.length.toLocaleString("lo-LA")} ລາຍການ
        </p>
        <div className="space-y-2">
          <Label htmlFor="bulk-category">ຫມວດ</Label>
          <SimpleSelect
            id="bulk-category"
            placeholder="ເລືອກຫມວດ"
            value={categoryValue}
            onValueChange={(v) => setCategoryValue(v ?? NONE)}
            options={options}
            className="w-full"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={bulk.isPending || ingredientIds.length === 0}
            onClick={() => void handleSubmit()}
          >
            {bulk.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
